import type { Severity } from 'Data/ViewModels/Core/types';
import { isNil } from 'ramda';

export type Message = {
  Id: string;
  MessageType: Severity;
  PropertyName: string;
  Text: string;
};

export type Limit = {
  PropertyName: string;
  Text: string;
  IsPassing: boolean;
  Message: Message;
};

export interface IApiDataModel {
  [key: string]: any | undefined;
  Messages: Message[];
  Limits: Limit[];
  Conflicts: string[];
}

type ApiDataModelProperty<TValue> = {
  path: string;
  key: string;
  value: TValue;
};

const isIApiDataModel = (arg: any): arg is IApiDataModel => arg && (arg.Messages || arg.Limits || arg.Conflicts);

/**
 * Recursively traverse an IApiDataModel with child IApiDataModels searching for a specific property.
 *
 * @param model Root model to be traversed.
 * @param path Start with empty string, used in the recursion.
 * @param key The property to select.
 */
const internalTraverseForProperty = <TValue>(
  model: IApiDataModel | null | undefined,
  path: string,
  key: string
): ApiDataModelProperty<TValue>[] => {
  let properties: ApiDataModelProperty<TValue>[] = [];

  if (isNil(model)) return properties;

  Object.entries(model).forEach(([subkey, value]) => {
    if (isIApiDataModel(value)) {
      properties = properties.concat(internalTraverseForProperty(value, `${path ? `${path}.` : ''}${subkey}`, key));
    }

    if (Array.isArray(value)) {
      value.forEach((val, index) => {
        properties = properties.concat(
          internalTraverseForProperty(val, `${path ? `${path}.` : ''}${subkey}[${index}]`, key)
        );
      });
    }
  });

  // Deliberately include null & empty array values - post-process the results if required.
  const value = model[key];
  if (value !== undefined) properties.push({ path, key, value });

  return properties;
};

/**
 * Represents all of the property names on every IApiDataModel
 * that represent additional data about other properties
 */
enum IApiDataModelMetaDataProperties {
  Conflicts = 'Conflicts',
  Messages = 'Messages',
  Limits = 'Limits',
}

export const isMetaDataProperty = (propertyName: string) => propertyName in IApiDataModelMetaDataProperties;

export const getConflicts = (model: IApiDataModel | null | undefined) =>
  internalTraverseForProperty<string[]>(model, '', IApiDataModelMetaDataProperties.Conflicts).filter(
    (conflicts) => conflicts.value.length > 0
  );

export const getMessages = (model: IApiDataModel | null | undefined) =>
  internalTraverseForProperty<Message[]>(model, '', IApiDataModelMetaDataProperties.Messages).filter(
    (messages) => messages.value.length > 0
  );
